import React, { useEffect, useMemo, useState } from "react";
import { API_URL } from "../../utils/config";
import { useUserContext } from "../../utils/UserContext";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate, formatDateTime } from "../../utils/Utilitats";
import DetallsAgenda from "./Detallsagenda";
import "./calendariagenda.css";
import "./seleccionaragenda.css";

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameYMD = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const NOMS_MESOS = [
  "Gener",
  "Febrer",
  "Març",
  "Abril",
  "Maig",
  "Juny",
  "Juliol",
  "Agost",
  "Setembre",
  "Octubre",
  "Novembre",
  "Desembre",
];

function generarDiesMes(any, mes) {
  const primer = (new Date(any, mes, 1).getDay() + 6) % 7;
  const numDies = new Date(any, mes + 1, 0).getDate();
  const cells = Array(primer).fill(null);
  for (let d = 1; d <= numDies; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function CalendariAgenda({ onTornar, onObrirAjuda, filters }) {
  const { empresaSeleccionada, usuariActiu } = useUserContext();

  const [currentMonth, setCurrentMonth] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [vistaCal, setVistaCal] = useState("mes");

  const [userOptions, setUserOptions] = useState([]);
  const [selectedUsuariLocal, setSelectedUsuariLocal] = useState(usuariActiu ?? null);
  const [mostraAvisosLocal, setMostraAvisosLocal] = useState(true);
  const [mostraTasquesLocal, setMostraTasquesLocal] = useState(true);

  const selectedUsuari = filters?.selectedUsuari ?? selectedUsuariLocal;
  const setSelectedUsuari = filters?.setSelectedUsuari ?? setSelectedUsuariLocal;
  const mostraAvisos = filters?.mostraAvisos ?? mostraAvisosLocal;
  const setMostraAvisos = filters?.setMostraAvisos ?? setMostraAvisosLocal;
  const mostraTasques = filters?.mostraTasques ?? mostraTasquesLocal;
  const setMostraTasques = filters?.setMostraTasques ?? setMostraTasquesLocal;

  const usuariCalendari =
    selectedUsuari && Number(selectedUsuari) !== 0 ? Number(selectedUsuari) : usuariActiu;

  useEffect(() => {
    const fetchUsuaris = async () => {
      if (!empresaSeleccionada?.IdEmpresa) {
        setUserOptions([]);
        return;
      }
      try {
        const resp = await fetchAutenticat(`${API_URL}/AgendaUsuaris.php`, {
          method: "POST",
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            ambit: "safata",
          }),
        });
        const data = await resp.json();
        const arr = Array.isArray(data?.usuaris) ? data.usuaris : [];
        const norm = arr
          .map((u) => ({
            idUsuaris: u.idUsuaris ?? u.idUsuari ?? u.Id ?? null,
            NomUsuari: u.NomUsuari ?? u.Nom ?? "",
          }))
          .filter((u) => u.idUsuaris != null);
        setUserOptions(norm);
      } catch (e) {
        if (e instanceof SessioCaducadaError) return;
        setUserOptions([]);
      }
    };
    fetchUsuaris();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const load = async () => {
      if (!empresaSeleccionada?.IdEmpresa || !usuariCalendari) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const resp = await fetchAutenticat(`${API_URL}/Agenda.php`, {
          method: "POST",
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            avisosUsuari: usuariCalendari,
            relacio: "assignades",
            estat: "tots",
          }),
        });
        const data = await resp.json();
        const arr = Array.isArray(data?.agenda) ? data.agenda : [];
        setItems(arr);
      } catch (e) {
        if (e instanceof SessioCaducadaError) return;
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [empresaSeleccionada, usuariCalendari, currentMonth]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (modal) return;
      onTornar?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal, onTornar]);

  const visibleItems = useMemo(
    () =>
      items.filter(
        (it) => (mostraAvisos && it.Tipus === "Avis") || (mostraTasques && it.Tipus === "Tasca")
      ),
    [items, mostraAvisos, mostraTasques]
  );

  const itemsByDay = useMemo(() => {
    const map = new Map();
    for (const t of visibleItems) {
      const startSrc = t.DataInici || (t.Tipus === "Avis" ? t.DataCreacio : null);
      if (!startSrc) continue;
      const start = new Date(startSrc);
      const end = t.DataFinal ? new Date(t.DataFinal) : start;
      const from = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const to = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      for (let d = new Date(from); d <= to; d = addDays(d, 1)) {
        const k = ymd(d);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(t);
      }
    }
    return map;
  }, [visibleItems]);

  const dayItems = (d) => itemsByDay.get(ymd(d)) || [];

  const openNew = (baseDate) => {
    setModal({
      mode: "nou",
      item: {
        Tipus: "Tasca",
        DataInici: `${ymd(baseDate)} 08:00`,
        DataFinal: null,
      },
    });
  };

  const openNewBlank = () => {
    setModal({
      mode: "nou",
      item: {
        Tipus: "Tasca",
        DataInici: null,
        DataFinal: null,
      },
    });
  };

  const openEdit = (it) => setModal({ mode: "modificar", item: it });

  const handleSaved = () => {
    setModal(null);
    setCurrentMonth(new Date(currentMonth));
  };

  const toggleAvisos = () => {
    if (mostraAvisos && !mostraTasques) return;
    setMostraAvisos(!mostraAvisos);
  };
  const toggleTasques = () => {
    if (mostraTasques && !mostraAvisos) return;
    setMostraTasques(!mostraTasques);
  };

  const headerLabel =
    vistaCal === "any"
      ? String(currentMonth.getFullYear())
      : currentMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const navegarAnterior = () => {
    setCurrentMonth((prev) =>
      vistaCal === "any"
        ? new Date(prev.getFullYear() - 1, prev.getMonth(), 1)
        : new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };
  const navegarSeguent = () => {
    setCurrentMonth((prev) =>
      vistaCal === "any"
        ? new Date(prev.getFullYear() + 1, prev.getMonth(), 1)
        : new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const renderCellaDia = (dataDia, compacte, key) => {
    const isSel = sameYMD(dataDia, selectedDay);
    const tks = dayItems(dataDia);
    return (
      <div
        key={key}
        className={`cal-cell ${compacte ? "cal-cell-petit" : ""} ${isSel ? "cal-sel" : ""}`}
        onClick={() => setSelectedDay(dataDia)}
        onDoubleClick={() => openNew(dataDia)}
        title={`${formatDate(dataDia)} — ${tks.length} elements`}
      >
        <div className="cal-daynum">{dataDia.getDate()}</div>
        {compacte ? (
          <div className="cal-dots">
            {tks.slice(0, 4).map((t) => (
              <span
                key={t.idAgenda}
                className={`cal-dot ${t.Tipus === "Tasca" ? "cal-dot-tasca" : "cal-dot-avis"}`}
                title={t.Titol || "(sense títol)"}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  openEdit(t);
                }}
              />
            ))}
          </div>
        ) : (
          <div className="cal-pills">
            {tks.slice(0, 3).map((t) => (
              <div
                key={t.idAgenda}
                className={`cal-pill cal-pill-${(t.Estat || "").toLowerCase()} ${
                  t.Tipus === "Tasca" ? "cal-pill-tasca" : "cal-pill-avis"
                }`}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  openEdit(t);
                }}
                title={`${t.Tipus === "Tasca" ? "Tasca" : "Avís"}: ${t.Titol || "(sense títol)"}${
                  t.DataInici ? ` · ${formatDateTime(t.DataInici)}` : ""
                }`}
              >
                {t.Titol || "(sense títol)"}
              </div>
            ))}
            {tks.length > 3 && <div className="cal-more">+{tks.length - 3}</div>}
          </div>
        )}
      </div>
    );
  };

  const renderGraellaMes = (anyMes, mesIdx, compacte) => (
    <div className={`cal-mes-bloc ${compacte ? "cal-mes-bloc-petit" : ""}`} key={`${anyMes}-${mesIdx}`}>
      {compacte && <div className="cal-mes-titol">{NOMS_MESOS[mesIdx]}</div>}
      <div className="cal-grid">
        {["Dl", "Dt", "Dc", "Dj", "Dv", "Ds", "Dg"].map((d) => (
          <div key={d} className="cal-dow">
            {d}
          </div>
        ))}
        {generarDiesMes(anyMes, mesIdx).map((diaNum, idx) =>
          diaNum ? renderCellaDia(new Date(anyMes, mesIdx, diaNum), compacte, idx) : (
            <div key={idx} className="cal-cell cal-cell-buit" />
          )
        )}
      </div>
    </div>
  );

  const nomUsuariSafata = userOptions.find(
    (u) => Number(u.idUsuaris) === Number(usuariCalendari)
  )?.NomUsuari;
  const veientAltre = usuariCalendari && Number(usuariCalendari) !== Number(usuariActiu);

  return (
    <div className="dv2-listBody Agenda-calendari-container">
      <div className="dv2-filterBar cal-toolbar">
        <div className="cal-nav">
          <button type="button" className="dv2-btn dv2-btn--icon" onClick={navegarAnterior}>
            ‹
          </button>
          <button
            type="button"
            className="dv2-btn"
            onClick={() => {
              const t = new Date();
              setCurrentMonth(new Date(t.getFullYear(), t.getMonth(), 1));
              setSelectedDay(new Date());
            }}
          >
            Avui
          </button>
          <button type="button" className="dv2-btn dv2-btn--icon" onClick={navegarSeguent}>
            ›
          </button>
        </div>

        <div className="cal-header-title">{headerLabel}</div>

        <div className="cal-filters">
          <div className="cal-vista-selector">
            <button
              type="button"
              className={`dv2-btn ${vistaCal === "mes" ? "dv2-btn--primary" : ""}`}
              onClick={() => setVistaCal("mes")}
            >
              Mes
            </button>
            <button
              type="button"
              className={`dv2-btn ${vistaCal === "any" ? "dv2-btn--primary" : ""}`}
              onClick={() => setVistaCal("any")}
            >
              Any complet
            </button>
          </div>
          <div className="cal-tipus-filtres">
            <button
              type="button"
              className={`Agenda-badge Agenda-badge-avis ${mostraAvisos ? "" : "Agenda-badge-inactiu"}`}
              onClick={toggleAvisos}
            >
              {mostraAvisos ? "✓ " : ""}
              {items.filter((it) => it.Tipus === "Avis").length} avisos
            </button>
            <button
              type="button"
              className={`Agenda-badge Agenda-badge-tasca ${mostraTasques ? "" : "Agenda-badge-inactiu"}`}
              onClick={toggleTasques}
            >
              {mostraTasques ? "✓ " : ""}
              {items.filter((it) => it.Tipus === "Tasca").length} tasques
            </button>
          </div>
          <label className="dv2-filter">
            <span className="dv2-filter-label">Usuari</span>
            <select
              className="dv2-select"
              value={usuariCalendari ?? ""}
              onChange={(e) => setSelectedUsuari(Number(e.target.value))}
            >
              {userOptions.map((u) => (
                <option key={u.idUsuaris} value={u.idUsuaris}>
                  {u.NomUsuari}
                  {usuariActiu && Number(u.idUsuaris) === Number(usuariActiu) ? " (jo)" : ""}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={openNewBlank}>
            + Tasca
          </button>
        </div>
      </div>

      {veientAltre && nomUsuariSafata && <p className="Agenda-safata-de">Agenda de: {nomUsuariSafata}</p>}

      {vistaCal === "mes" ? (
        renderGraellaMes(currentMonth.getFullYear(), currentMonth.getMonth(), false)
      ) : (
        <div className="cal-any-graella">
          {NOMS_MESOS.map((_, idx) => renderGraellaMes(currentMonth.getFullYear(), idx, true))}
        </div>
      )}

      <div className="cal-day-panel">
        <div className="cal-day-title">
          Elements de {formatDate(selectedDay)} ({dayItems(selectedDay).length})
        </div>
        {loading ? (
          <p className="dv2-hint">Carregant…</p>
        ) : dayItems(selectedDay).length === 0 ? (
          <p className="dv2-empty">Cap element assignat per aquest dia. Doble clic al calendari per crear una tasca.</p>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th>Tipus</th>
                  <th>Inici</th>
                  <th>Final</th>
                  <th>Títol</th>
                  <th>Estat</th>
                  <th>Dirigit a</th>
                </tr>
              </thead>
              <tbody>
                {dayItems(selectedDay).map((t) => (
                  <tr
                    key={t.idAgenda}
                    className={t.Tipus === "Tasca" ? "Agenda-fila-tasca" : "Agenda-fila-avis"}
                    onDoubleClick={() => openEdit(t)}
                    title="Doble clic per modificar"
                  >
                    <td>
                      <span
                        className={`Agenda-badge ${
                          t.Tipus === "Tasca" ? "Agenda-badge-tasca" : "Agenda-badge-avis"
                        }`}
                      >
                        {t.Tipus === "Tasca" ? "Tasca" : "Avís"}
                      </span>
                    </td>
                    <td>{t.DataInici ? formatDateTime(t.DataInici) : "—"}</td>
                    <td>{t.DataFinal ? formatDateTime(t.DataFinal) : "—"}</td>
                    <td>{t.Titol || "—"}</td>
                    <td>{t.Estat || "—"}</td>
                    <td>{t.NomDirigitA || t.DirigitA || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div
          className="cal-modal"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setModal(null);
          }}
        >
          <div className="cal-modal-content">
            <DetallsAgenda
              variant="modal"
              mode={modal.mode}
              item={modal.item}
              onSave={handleSaved}
              onCancel={() => setModal(null)}
              onObrirAjuda={onObrirAjuda}
              forceTasca
            />
          </div>
        </div>
      )}
    </div>
  );
}
